import { memo, useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DeviceMonitorControl = memo(
    ({
        currentSignalKey,
        signalTypeInfo,
        isExpanded,
        currentSignalIndex,
        totalSignals,
        onPrevSignal,
        onNextSignal,
        isRefreshing,
        initialValue,
        onValueChange,
    }) => {
        // Convert raw value (0-1023) to percentage (0-100)
        const rawToPercentage = (rawValue) => {
            return ((rawValue || 0) / 1023) * 100;
        };

        // Convert percentage (0-100) to raw value (0-1023)
        const percentageToRaw = (percentage) => {
            return Math.round((percentage / 100) * 1023);
        };

        const [rangeValue, setRangeValue] = useState(
            rawToPercentage(initialValue)
        );
        const [toggleValue, setToggleValue] = useState(
            rawToPercentage(initialValue)
        );
        const sliderUpdateTimeout = useRef(null);

        // Update local state when initialValue changes
        useEffect(() => {
            const percentage = rawToPercentage(initialValue);
            setRangeValue(percentage);
            setToggleValue(percentage);
        }, [initialValue]);

        const handleRangeChange = (e) => {
            const percentageVal = parseInt(e.target.value);
            setRangeValue(percentageVal);

            if (sliderUpdateTimeout.current) {
                clearTimeout(sliderUpdateTimeout.current);
            }

            sliderUpdateTimeout.current = setTimeout(() => {
                const rawVal = percentageToRaw(percentageVal);
                onValueChange(rawVal);
            }, 500);
        };

        const handleToggleClick = () => {
            // Toggle based on percentage: < 50% = OFF (0), >= 50% = ON (1023)
            const newPercentage = toggleValue < 50 ? 100 : 0;
            const newRawVal = percentageToRaw(newPercentage);
            setToggleValue(newPercentage);
            onValueChange(newRawVal);
        };

        return (
            <div
                className={`tw-flex tw-items-center tw-justify-between tw-border-b ${
                    isExpanded ? "tw-p-6" : "tw-p-1"
                }`}
            >
                <button
                    className={`tw-rounded-full tw-text-white tw-transition-colors ${
                        isExpanded ? "tw-p-2" : "tw-p-1"
                    } ${
                        currentSignalIndex === 0
                            ? "tw-bg-gray-300 tw-cursor-not-allowed"
                            : "tw-bg-blue-500 hover:tw-bg-blue-600"
                    }`}
                    onClick={onPrevSignal}
                    disabled={currentSignalIndex === 0}
                >
                    <ChevronLeft
                        className={`${
                            isExpanded ? "tw-h-6 tw-w-6" : "tw-h-4 tw-w-4"
                        }`}
                    />
                </button>

                <div
                    className={`tw-flex-1 tw-flex ${
                        isExpanded
                            ? "tw-flex-row tw-items-center tw-justify-between tw-px-6"
                            : "tw-flex-col tw-items-center"
                    }`}
                >
                    {/* Left side - Signal name and chips */}
                    <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
                        <div
                            className={`tw-font-bold tw-text-gray-800 ${
                                isExpanded ? "tw-text-2xl" : "tw-text-sm"
                            }`}
                        >
                            {currentSignalKey}
                        </div>
                        {isExpanded && (
                            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3">
                                {signalTypeInfo.analog && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-purple-100 tw-text-purple-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        Proportional
                                    </span>
                                )}
                                {signalTypeInfo.digital && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-green-100 tw-text-green-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        Discrete
                                    </span>
                                )}
                                {signalTypeInfo.input && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-blue-100 tw-text-blue-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        Sense
                                    </span>
                                )}
                                {signalTypeInfo.output && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-orange-100 tw-text-orange-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        Actuate
                                    </span>
                                )}
                                {signalTypeInfo.status && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-yellow-100 tw-text-yellow-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        Status
                                    </span>
                                )}
                                {signalTypeInfo.control && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-red-100 tw-text-red-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        Control
                                    </span>
                                )}
                                {signalTypeInfo.GPIO && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-gray-100 tw-text-gray-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        GPIO Bus
                                    </span>
                                )}
                                {signalTypeInfo.UART && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-indigo-100 tw-text-indigo-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        UART Bus
                                    </span>
                                )}
                                {signalTypeInfo.I2C && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-teal-100 tw-text-teal-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        I2C Bus
                                    </span>
                                )}
                                {signalTypeInfo.VIRTUAL && (
                                    <span className="tw-px-3 tw-py-1 tw-bg-slate-100 tw-text-slate-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                        Virtual
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Center - Range slider for analog (output OR input) */}
                    {isExpanded &&
                        signalTypeInfo.analog &&
                        (signalTypeInfo.output || signalTypeInfo.input) && (
                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-8 tw-w-80">
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="1"
                                    title={`${rangeValue}%`}
                                    value={rangeValue}
                                    onChange={
                                        signalTypeInfo.output
                                            ? handleRangeChange
                                            : undefined
                                    }
                                    disabled={signalTypeInfo.input}
                                    className={`tw-w-full tw-h-2 ${
                                        signalTypeInfo.output
                                            ? "tw-cursor-pointer tw-slider-thumb-custom"
                                            : "tw-cursor-not-allowed tw-opacity-60"
                                    }`}
                                />
                                <div className="tw-flex tw-justify-between tw-text-gray-500 tw-mt-2 tw-text-sm tw-w-full">
                                    <span>0%</span>
                                    <span className="tw-text-xs tw-text-gray-600">
                                        {rangeValue.toFixed(1)}%
                                    </span>
                                    <span>100%</span>
                                </div>
                                <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-1">
                                    {signalTypeInfo.input
                                        ? "Input signals cannot be controlled"
                                        : "Drag to set the value"}
                                </div>
                            </div>
                        )}

                    {/* Center - Toggle for digital (output OR input) */}
                    {isExpanded &&
                        signalTypeInfo.digital &&
                        (signalTypeInfo.output || signalTypeInfo.input) && (
                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
                                <button
                                    onClick={
                                        signalTypeInfo.output
                                            ? handleToggleClick
                                            : undefined
                                    }
                                    disabled={signalTypeInfo.input}
                                    className={`tw-relative tw-inline-flex tw-h-10 tw-w-20 tw-items-center tw-rounded-full tw-transition-colors tw-duration-300 ${
                                        toggleValue >= 50
                                            ? "tw-bg-green-500"
                                            : "tw-bg-gray-300"
                                    } ${
                                        signalTypeInfo.input
                                            ? "tw-cursor-not-allowed tw-opacity-60"
                                            : "tw-cursor-pointer"
                                    }`}
                                >
                                    <span
                                        className={`tw-inline-block tw-h-8 tw-w-8 tw-transform tw-rounded-full tw-bg-white tw-transition-transform tw-duration-300 tw-shadow-md ${
                                            toggleValue >= 50
                                                ? "tw-translate-x-11"
                                                : "tw-translate-x-1"
                                        }`}
                                    />
                                </button>
                                <div className="tw-text-xs tw-text-gray-600 tw-mt-2 tw-font-medium">
                                    {toggleValue >= 50 ? "ON" : "OFF"}
                                </div>
                                <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-1">
                                    {signalTypeInfo.input
                                        ? "Input controls cannot be controlled"
                                        : toggleValue >= 50
                                        ? "Output is ON, click to turn OFF"
                                        : "Output is OFF, click to turn ON"}
                                </div>
                            </div>
                        )}

                    {/* Right side - Signal count */}
                    {isExpanded && totalSignals > 0 && (
                        <div className="tw-flex tw-items-center tw-text-sm tw-text-gray-500 tw-whitespace-nowrap">
                            {currentSignalIndex + 1} of {totalSignals} signals
                        </div>
                    )}
                </div>

                <button
                    className={`tw-rounded-full tw-text-white tw-transition-colors ${
                        isExpanded ? "tw-p-2" : "tw-p-1"
                    } ${
                        currentSignalIndex >= totalSignals - 1
                            ? "tw-bg-gray-300 tw-cursor-not-allowed"
                            : "tw-bg-blue-500 hover:tw-bg-blue-600"
                    }`}
                    onClick={onNextSignal}
                    disabled={currentSignalIndex >= totalSignals - 1}
                >
                    <ChevronRight
                        className={`${
                            isExpanded ? "tw-h-6 tw-w-6" : "tw-h-4 tw-w-4"
                        }`}
                    />
                </button>
            </div>
        );
    }
);

DeviceMonitorControl.displayName = "DeviceMonitorControl";

export default DeviceMonitorControl;
